// React imports
import { useEffect, useState, useRef, useCallback } from 'react';

// Third-party library imports
import { useQuery, useMutation } from '@tanstack/react-query';
import { Tooltip } from 'react-tooltip';
import { View } from 'ol/index';
import ImageLayer from 'ol/layer/Image';
import Static from 'ol/source/ImageStatic';
import { Projection } from 'ol/proj';
import { BiSave } from 'react-icons/bi';
import { VscWand } from 'react-icons/vsc';
import { BsSkipEnd } from 'react-icons/bs';
// import pLimit from 'p-limit';

import ButtonGroup from 'react-bootstrap/ButtonGroup';

// Contexts
import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import { useConfirm } from '@contexts/Confirm.context';

// Constants
import { COORDINATE_SYSTEM, MODEL_TYPE } from '@utils/constants';

// Hooks
import useNotify from '@hooks/useNotify';
import useKeyboardShortcut from '@hooks/useKeyboardShortcut.hook';

// Utils
import {
	setLayersVisibilityByCustomIds,
	addMapLoadingSpinner,
	removeMapLoadingSpinner,
} from '@utils/map/helpers';
import {
	addDrawLayer,
	drawEndInteraction,
	drawStartInteraction,
	deleteAnnotation,
	addExistingAnnotations,
	parseFeatureToAnnotation,
	getNormalizedPolygonByExtent,
} from '@utils/map/singleImage.draw';
import { setDrawInteraction } from '@utils/map/singleImage.draw';
import { removeErasorHighlightedFeature } from '@utils/map/erasorPointer';
import { setupCopyPasteShortcuts } from '@utils/map/annotation/copyPaste.interaction';
import { setModifyInteraction } from '@utils/map/annotation/modify.interaction';

// Components
import MapView from './AnnotateSingleImagesMap';
import {
	AnnotationToolBar,
	KeyboardShortcut,
} from '@routes/userRoutes/projects/singleProject/components/SharedStyles';
import UtilityButton from './utilityButtons/UtilityButton';
import AnnotationDrawTools from './utilityButtons/AnnotationDrawTools';

// API
import {
	postTileData,
	initTraining,
	fetchUrl,
	getAnnotationInfoForModel,
	addAnnotations,
	deleteAnnotations,
} from '@utils/api';

const groupFeaturesByName = features => {
	const featuresByName = features.reduce((acc, feature) => {
		const name = feature.get('name');
		acc[name] = feature; // Assuming each name is unique
		return acc;
	}, {});

	return featuresByName;
};

export default function AnnotateSingleImages({
	layerId = 'annotatePhotosLayer',
	layersToShow,
	isDSPMode = false,
}) {
	const {
		mapObject,
		project,
		pickedTask,
		singleImageFeatures,
		modelType,
		annotationDrawTool,
		annotationMode,
		annotationSidebarData,
		toolBarVisible,
		confirmModalContent,
		colorOptions,
		isRotateEnabled,
		dispatch,
	} = useProject();

	const { gridFeatures: imagesInAreaOfInterest } = annotationMode;
	const groupedFeatures = groupFeaturesByName(singleImageFeatures);

	const { checkForNotifications } = useNotify();
	const { addToast } = useToast();
	const { confirm } = useConfirm();

	// Because this type of annotation needs a nother type of projection
	// we need to create a new map and will not use the map from the context
	const [map, setMap] = useState(null);

	// States to show loading spinner and disable tools
	const [loading, setLoading] = useState(false);
	const [disableTools, setDisableTools] = useState(false);

	// State to know if the user wants to hide the dialog
	const [hideDialog, setHideDialog] = useState(false);

	// State to keep track of the active image
	const [activeImage, setActiveImage] = useState({
		index: 0,
		feature: imagesInAreaOfInterest[0],
		extent: null,
	});

	// Layers added to the map
	const [imageLayer, setImageLayer] = useState(null);
	const [drawLayer, setDrawLayer] = useState(null);

	const drawType = modelType === MODEL_TYPE.OBJECT_DETECTION ? 'Circle' : 'Polygon';

	// State to keep track of the drawing or erasing
	const [drawing, setDrawing] = useState(true);

	//Refs
	const isInitiated = useRef(false); // To prevent re-rendering
	const activeClassId = useRef(annotationSidebarData?.activeClassId ?? 0);
	const activeTileName = useRef(imagesInAreaOfInterest[0]?.properties?.tile_name ?? null);
	const copyPasteCleanup = useRef(null); // Store cleanup function for copy-paste
	const modifyListenersCleanup = useRef(null); // Store cleanup function for modify listeners

	// ref for aborting image requests
	const imageAbortControllerRef = useRef(null);

	// Flag to prevent spinner from showing when swapping to full-res
	const isLoadingFullResImage = useRef(false);

	const { mutate: startTraining } = useMutation({
		mutationFn: () =>
			initTraining({
				projectUuid: project.uuid,
				modelId: pickedTask.model_uuid,
			}),
		onError: err => {
			console.error('Error starting training from single image annotate', err);
			addToast({
				id: 'training-error',
				className: 'bg-danger',
				title: `Training model '${pickedTask.description}' failed:`,
				message:
					err.response?.data?.detail || err.message || 'Unknown error starting training.',
				autohide: false,
			});
		},
		onSuccess: res => {
			if (res?.warning) {
				addToast({
					id: 'training-warning',
					className: 'bg-warning',
					title: `Training model '${pickedTask.description}' started with warnings:`,
					message: res.warning,
					autohide: true,
				});
			} else if (res?.error) {
				addToast({
					id: 'training-error',
					className: 'bg-danger',
					title: `Training model '${pickedTask.description}' failed:`,
					message: res.error,
					autohide: false,
				});
			} else {
				addToast({
					id: 'training-success',
					className: 'bg-success',
					title: `Training model '${pickedTask.description}' started successfully`,
					message: 'The process will take some time to complete.',
					autohide: true,
				});
			}
		},

		onSettled: () => {
			stopAnnotation();
			setTimeout(() => checkForNotifications(), 5000);
		},
	});

	// End annotation session and start training
	const endAnnotation = async () => {
		const confirmContinue = await saveTileData();
		if (confirmContinue) startTraining();
	};

	const saveAndExit = async () => {
		const confirmContinue = await saveTileData();
		if (confirmContinue) stopAnnotation();
	};

	// TODO: This code is expected to be re-made and improved in the future.
	const saveAndApply = async () => {
		/**
		 * Compares two arrays to identify elements that were added, removed, or modified
		 *
		 * @param {Array} existing - Array of objects with a properties.annotation_uuid property
		 * @param {Array} current - Array of objects with a getProperties() method that returns an object with a uuid property
		 * @returns {Object} An object containing:
		 *   - removedAnnotationUuids: UUIDs from the existing array that don't have matching UUIDs in the current array
		 *   - addedAnnotations: Elements from the current array that don't have matching UUIDs in the existing array
		 */
		const getDifference = (existing, current) => {
			const currentUuids = new Set(current.map(a => a?.getProperties()?.uuid));
			const removedAnnotationUuids = [];
			for (const item of existing) {
				const uuid = item?.properties?.annotation_uuid;
				if (!currentUuids.has(uuid)) removedAnnotationUuids.push(uuid);
			}

			const existingUuids = new Set(existing.map(a => a?.properties?.annotation_uuid));
			const addedAnnotations = current.filter(
				a => !existingUuids.has(a?.getProperties()?.uuid)
			);

			const modifiedAnnotations = current.filter(
				a => existingUuids.has(a?.getProperties()?.uuid) && a?.getProperties()?.isModified
			);

			return { removedAnnotationUuids, addedAnnotations, modifiedAnnotations };
		};

		const confirmContinue = await saveTileData();
		if (!confirmContinue) return;
		else setLoading(true);

		// If no existing/current annotations found get empty arrays
		const existingAnnotations = getExistingTileAnnotations() ?? [];
		const currentAnnotations = getCurrentTileAnnotations() ?? [];

		const { removedAnnotationUuids, addedAnnotations, modifiedAnnotations } =
			getDifference(existingAnnotations, currentAnnotations) ?? {};

		// TODO: refactor this block when the backend add 'modifyAnnotations' API
		for (const modifiedAnnotation of modifiedAnnotations) {
			removedAnnotationUuids.push(modifiedAnnotation?.getProperties()?.uuid);
			addedAnnotations.push(modifiedAnnotation);
		}

		if (removedAnnotationUuids?.length) {
			try {
				await deleteAnnotations({
					projectUuid: project.uuid,
					modelUuid: pickedTask.model_uuid,
					annotationUuids: removedAnnotationUuids,
				});
			} catch (error) {
				console.error('Failed to delete detections:', error);
				addToast({
					id: 'delete-detections-error',
					className: 'bg-danger',
					title: 'Failed to delete detections',
					message: 'Please contact support if this issue persists.',
					autohide: false,
				});
			}
		}

		if (addedAnnotations?.length) {
			try {
				const polygons = [];

				for (const annotationFeature of addedAnnotations) {
					const { classId } = annotationFeature.getProperties() ?? {};
					const { class: classname } = pickedTask?.classes[classId] ?? {};

					const normalizedPolygon = getNormalizedPolygonByExtent(
						activeImage.extent,
						annotationFeature
					);

					polygons.push({
						classid: classId,
						classname,
						polygon: normalizedPolygon,
					});
				}

				if (polygons.length === 0) {
					console.warn('No valid polygons to add');
				} else {
					await addAnnotations({
						crs: COORDINATE_SYSTEM.PIXEL,
						projectUuid: project.uuid,
						filename: activeTileName.current,
						modelUuid: pickedTask?.model_uuid,
						polygons,
					});
				}
			} catch (error) {
				console.error('Failed to add detections:', error);
				addToast({
					id: 'add-detections-error',
					className: 'bg-danger',
					title: 'Failed to add detections',
					message: 'Please contact support if this issue persists.',
					autohide: false,
				});
			}
		}

		setLoading(false);

		stopAnnotation();
	};

	// Stop annotation session
	const stopAnnotation = () => {
		if (map) {
			//Destroy the map
			map.setTarget(null);
			map.dispose();
		}

		setMap(null);
		setActiveImage(null);
		setImageLayer(null);
		setDrawLayer(null);

		dispatch({
			type: 'setDialogue',
			payload: null,
		});
		dispatch({
			type: 'setToolBarVisible',
			payload: true,
		});
		dispatch({
			type: 'setAnnotationMode',
			payload: null,
		});
		if (isDSPMode)
			dispatch({
				type: 'setSelectedSingleImage',
				payload: null,
			});

		setLayersVisibilityByCustomIds(mapObject, layersToShow, true);
	};

	// Check if there are annotations in the current image before skipping
	const checkImageBeforeSkip = async () => {
		setLoading(true);

		const { source: drawSource } = drawLayer || {};
		if (!drawSource) {
			setLoading(false);
			return false;
		}

		let confirmContinue = true;
		const features = drawSource.getFeatures();
		if (features.length > 0) {
			const userConfirmed = await confirm({
				title: 'Discard Annotations',
				message:
					'You have made annotations in this image. Skipping will discard them. Still good to go?',
			});

			if (!userConfirmed) confirmContinue = false;
		}

		setLoading(false);
		return confirmContinue;
	};

	// Save the annotations in the current square
	const saveTileData = async () => {
		setLoading(true);

		const { source: drawSource } = drawLayer || {};
		if (!drawSource) {
			setLoading(false);
			return false;
		}

		const features = drawSource.getFeatures();
		const annotations = features.map(feature =>
			parseFeatureToAnnotation({
				feature,
				taskId: pickedTask.model_uuid,
				modelType,
				tile_name: activeTileName.current,
				classId: activeClassId.current,
				currentImageExtent: activeImage.extent,
			})
		);

		if (annotations.length === 0) {
			const userConfirmed = await confirm({
				title: 'No Annotations',
				message:
					"No new annotations were made in this image. It could affect the model if something's missed. Still good to go?",
			});

			if (!userConfirmed) {
				setLoading(false);
				return false;
			}
		}

		console.log('Submit Tile: ', annotations);
		try {
			await postTileData(
				project.uuid,
				pickedTask.model_uuid,
				activeTileName.current,
				annotations
			);
			return true;
		} catch (error) {
			return !!console.error('Error saving single image annotation data', error);
		} finally {
			setLoading(false);
		}
	};

	// Create image loader with abort support
	const createImageLoader = signal => (image, src) => {
		const img = image.getImage();

		const abortHandler = () => (img.src = '');

		if (signal.aborted) return abortHandler();

		signal.addEventListener('abort', abortHandler, { once: true });

		const loadHandler = () => signal.removeEventListener('abort', abortHandler);
		const errorHandler = () => signal.removeEventListener('abort', abortHandler);

		img.addEventListener('load', loadHandler, { once: true });
		img.addEventListener('error', errorHandler, { once: true });

		img.src = src;
	};

	// Fetch the full resolution image and update the layer
	const fetchFullImage = (fullImage, imageExtent, projection, layer) => {
		if (!fullImage?.url) {
			console.error('Could not get full resolution images from s3.');
			isLoadingFullResImage.current = false;
			return;
		}

		// Create new AbortController for full-res request
		const fullResImageSignal = imageAbortControllerRef.current.signal;

		// Create a new source for full resolution image
		const fullResImageSource = new Static({
			url: fullImage.url,
			imageExtent: imageExtent,
			projection: projection,
			imageLoadFunction: createImageLoader(fullResImageSignal),
		});

		const handleFullResImageLoad = event => {
			// Check if request was aborted
			if (fullResImageSignal.aborted) {
				isLoadingFullResImage.current = false;
				return;
			}

			const { type } = event;
			if (type === 'imageloaderror') console.error('Full resolution image load error');

			// Reset flag
			isLoadingFullResImage.current = false;

			// Cleanup listeners once (regardless of success or error)
			fullResImageSource.un('imageloadend', handleFullResImageLoad);
			fullResImageSource.un('imageloaderror', handleFullResImageLoad);
		};

		// Attach event listeners
		fullResImageSource.on('imageloadend', handleFullResImageLoad);
		fullResImageSource.on('imageloaderror', handleFullResImageLoad);

		// Set flag before swapping source
		isLoadingFullResImage.current = true;

		// Swap the source to full resolution image
		layer.setSource(fullResImageSource);
	};

	// Change the image to annotate
	const onChangeImage = async (layer, index) => {
		if (index < 0 || index >= imagesInAreaOfInterest.length) return;

		// Cancel any ongoing image load requests
		if (imageAbortControllerRef.current) {
			imageAbortControllerRef.current.abort();
			imageAbortControllerRef.current = null;
		}

		// Reset the flag when starting a new image load
		isLoadingFullResImage.current = false;

		// Create new AbortController for image request
		imageAbortControllerRef.current = new AbortController();
		const compressedImageSignal = imageAbortControllerRef.current.signal;

		dispatch({
			type: 'setDialogue',
			payload: {
				header: 'Loading Image',
				body: 'Load time depends on the image size and resolution. Please wait...',
				fullWidth: false,
			},
		});

		try {
			setDisableTools(true);

			const feature = imagesInAreaOfInterest[index];
			const { source: drawSource } = drawLayer || {};

			// Clear any existing annotations
			if (drawSource) drawSource.clear();
			layer.setSource(null);

			const { tile_name: imageName, image_dimensions } = feature.properties;
			const { height_pixel: height, width_pixel: width } = image_dimensions;

			// Fetch both URLs (not the actual images yet)
			const [compressedImage, fullImage] = await Promise.all([
				fetchUrl(`filelink?key=${project.uuid}/images/compressed/${imageName}`),
				fetchUrl(`filelink?key=${project.uuid}/images/${imageName}`),
			]);

			activeTileName.current = imageName;

			// Check if aborted during fetch
			if (compressedImageSignal.aborted) return;

			if (!compressedImage?.url && !fullImage?.url) {
				console.error('Could not get images from s3. Trying the next image.');
				onChangeImage(layer, index + 1);
				return;
			}

			// Get the view and the current center
			const center = [0, 0];

			// Calculate the new extent based on the center
			const imageExtent = [
				center[0] - width / 2,
				center[1] - height / 2,
				center[0] + width / 2,
				center[1] + height / 2,
			];

			setActiveImage({
				index,
				feature,
				extent: imageExtent,
			});

			const projection = new Projection({
				code: 'xkcd-image',
				units: 'pixels',
				extent: imageExtent,
			});

			const source = new Static({
				url: compressedImage?.url,
				imageExtent: imageExtent,
				projection: projection,
				imageLoadFunction: createImageLoader(compressedImageSignal),
			});

			const newView = new View({
				projection: projection,
				center: center,
				zoom: 1,
			});

			map.setView(newView);

			// Handle compressed image load events
			const handleCompressedImageLoad = event => {
				// Check if request was aborted
				if (compressedImageSignal.aborted) return;

				const { type } = event;

				if (type === 'imageloadend') {
					if (hideDialog) {
						dispatch({
							type: 'setDialogue',
							payload: null,
						});
					} else {
						dispatch({
							type: 'setDialogue',
							payload: {
								header: !isDSPMode
									? 'Annotating'
									: 'Annotating Detections & Training Data',
								body: (
									<>
										{!isDSPMode ? (
											<>
												<p>
													Annotate all relevant items within the image
													before clicking "Next Image".
												</p>
												<p className="small text-muted">
													Also use "Next Image" on images without target
													items to help the model learn to recognize empty
													areas. Only use "Skip" if you don't want to
													train the model on this image.
												</p>
											</>
										) : (
											<p className="small text-muted">
												Note: When you add or remove annotations, the
												corresponding detections will be updated on the map
												and saved as training data to help improve model
												accuracy.
											</p>
										)}
									</>
								),
								fullWidth: false,
								autoClose: true,
								dismissible: true,
								onDismiss: () => {
									setHideDialog(true);
								},
							},
						});
					}

					// Removing the map loader to not block the UI when full image is loading
					removeMapLoadingSpinner(map);
					// Using map render complete to ensure the compressed image is fully rendered
					map.once('rendercomplete', () => {
						if (!compressedImageSignal.aborted) {
							fetchFullImage(fullImage, imageExtent, projection, layer);
						}
					});
				}

				if (type === 'imageloaderror') {
					console.error('Could not add image to layer. Trying the next image.');
					onChangeImage(layer, index + 1);
				}

				// Cleanup listeners once (regardless of success or error)
				source.un('imageloadend', handleCompressedImageLoad);
				source.un('imageloaderror', handleCompressedImageLoad);
			};

			// Attach event listeners
			source.on('imageloadend', handleCompressedImageLoad);
			source.on('imageloaderror', handleCompressedImageLoad);

			layer.setSource(source);

			// Fit the view to the extent
			newView.fit(imageExtent, {
				padding: [50, 50, 50, 50],
				duration: 0,
			});
		} catch (e) {
			console.error('Could not get images from s3', e);
			isLoadingFullResImage.current = false;
		}
	};

	const addDrawingInteraction = ({ drawSetup, layer }) => {
		const { draw, translate, modify } = drawSetup;

		// Need to activate/deactivate the interactions to be able to draw on top of other features
		const activateInteractions = bool => {
			translate.setActive(bool);
			modify.setActive(bool);
		};

		// Add drawstart interaction
		draw?.on('drawstart', e => {
			drawStartInteraction({
				e,
				layer,
				draw,
				classId: activeClassId.current,
				activateInteractions,
			});
		});

		// Add drawend interaction
		draw?.on('drawend', e => {
			drawEndInteraction({
				e,
				classId: activeClassId.current,
				modelType,
				activateInteractions,
			});
		});
	};

	/**
	 * Retrieves existing annotations for the currently active tile.
	 *
	 * @returns {Array|null} An array of annotation features for the active tile,
	 *                      or null if no active tile or annotations exist.
	 */
	const getExistingTileAnnotations = () => {
		const imagePoint = groupedFeatures[activeTileName?.current];
		if (!imagePoint) return null;

		const activeTileDetections = imagePoint?.get('detections');
		if (!activeTileDetections) return null;

		return activeTileDetections?.features ?? null;
	};

	/**
	 * Retrieves current annotations/features from the currently active tile's drawLayer source.
	 *
	 * @returns {Array|null} An array of annotations from the current tile's draw layer source,
	 * or null if the source doesn't exist or has no features.
	 */
	const getCurrentTileAnnotations = () => {
		const { source } = drawLayer ?? {};
		if (!source) return null;

		const features = source?.getFeatures();
		return features ?? null;
	};

	/**
	 * Add any posible existing annotations to the map
	 */
	const addExistingAnnotationsToMap = async () => {
		// Check if activeImage has an extent property, if not, return early
		if (!activeImage?.extent) return;

		// Retrieve existing annotations for the active image's tile name
		const existingAnnotations = getExistingTileAnnotations();

		// If there are existing annotations, proceed to add them to the map
		if (existingAnnotations) {
			// Function to get the draw source, waiting if necessary
			const getDrawSource = async () => {
				const { source: drawSource } = drawLayer || {};
				// If the draw source is not available, wait for it to be available
				if (!drawSource) {
					await new Promise(resolve => {
						setTimeout(() => {
							resolve();
						}, 1000); // Wait for 1 second before checking again
					});
					return getDrawSource(); // Recursively call getDrawSource until drawSource is available
				}
				return drawSource; // Return the draw source once available
			};

			// Await the draw source to be available
			const drawSource = await getDrawSource();

			// Add existing annotations to the map using the draw source
			addExistingAnnotations({
				source: drawSource,
				annotations: existingAnnotations,
				imageExtent: activeImage.extent,
				modelType,
			});
		}
	};

	// Move to the next image
	const nextImage = async ({ skip = false }) => {
		if (loading) return;

		if (activeImage.index < imagesInAreaOfInterest.length - 1) {
			let confirmContinue = true;

			if (skip) confirmContinue = await checkImageBeforeSkip();
			else confirmContinue = await saveTileData();

			if (confirmContinue) onChangeImage(imageLayer, activeImage.index + 1);
		} else if (activeImage.index === imagesInAreaOfInterest.length - 1) endAnnotation();
	};

	// Start drawing
	const startDraw = () => {
		setDrawing(true);
		const { draw, select, translate, modify, pointerMoveRef } = drawLayer;
		draw.setActive(true);
		translate.setActive(true);
		modify.setActive(true);
		select.setActive(false);

		// Remove the pointermove event listener
		map.un('pointermove', pointerMoveRef);

		// Ensure the pointer style is set back to normal so we know the erasor icon is gone
		const mapTarget = map.getTargetElement();
		if (mapTarget) mapTarget.style.cursor = 'default';

		// Remove any highlighted feature
		removeErasorHighlightedFeature();
	};

	// Start erasing
	const startErase = () => {
		setDrawing(false);
		const { draw, select, translate, modify, pointerMoveRef } = drawLayer;
		draw.setActive(false);
		translate.setActive(false);
		modify.setActive(false);

		select.setActive(true);
		map.on('pointermove', pointerMoveRef);
	};

	// Handle Exit button with confirmation modal
	const handleExit = async () => {
		// In DSP mode, just show simple exit confirmation
		if (isDSPMode) {
			const userConfirmed = await confirm({
				title: 'Exit annotation mode',
				message: 'Are you sure you want to exit annotation mode?',
			});

			if (userConfirmed) stopAnnotation();
			return;
		}

		const result = await confirm({
			title: 'Exit annotation mode',
			message: (
				<p>
					Previous images are saved automatically. <br />
					Do you want to save your current image before leaving?
				</p>
			),
			confirmLabel: 'Save & Exit',
			cancelLabel: 'Exit',
		});

		if (result === true) {
			// Save & Exit (Confirm button clicked)
			await saveAndExit();
		} else if (result === false) {
			// Exit Without Saving (Cancel button clicked)
			stopAnnotation();
		}
		// If result === null, user dismissed the modal (X button) - do nothing, stay in annotation mode
	};

	// Keyboard shortcuts
	useKeyboardShortcut('Enter', !!annotationMode && confirmModalContent === null, () =>
		nextImage({ skip: false })
	);
	useKeyboardShortcut(' ', !!annotationMode && confirmModalContent === null, () =>
		nextImage({ skip: true })
	);
	useKeyboardShortcut('Escape', !toolBarVisible && confirmModalContent === null, () =>
		handleExit()
	);

	/**
	 * Attaches event listeners to modify and translate interactions for tracking changes
	 * @param {Object} modifyInteraction - The modify interaction instance
	 * @param {Object} translateInteraction - The translate interaction instance
	 * @param {Function} setSelectedFeature - Setter for selected feature (for copy/paste)
	 * @returns {Function} Cleanup function to remove all attached listeners
	 */
	const attachModifyListeners = useCallback(
		(modifyInteraction, translateInteraction, setSelectedFeature) => {
			const markAsModified = feature => {
				const uuid = feature.getProperties()?.uuid;
				if (uuid) feature.set('isModified', true);
			};

			// Define handler functions so they can be removed later
			const modifyStartHandler = event => {
				const feature = event.features.item(0);
				if (setSelectedFeature) setSelectedFeature(feature);
			};

			const modifyEndHandler = event => {
				const feature = event.features.item(0);
				markAsModified(feature);
				if (setSelectedFeature) setSelectedFeature(feature);
			};

			const translateStartHandler = event => {
				const feature = event.features.item(0);
				if (setSelectedFeature) setSelectedFeature(feature);
			};

			const translateEndHandler = event => {
				const feature = event.features.item(0);
				markAsModified(feature);
				if (setSelectedFeature) setSelectedFeature(feature);
			};

			// Attach listeners
			modifyInteraction.on('modifystart', modifyStartHandler);
			modifyInteraction.on('modifyend', modifyEndHandler);
			translateInteraction.on('translatestart', translateStartHandler);
			translateInteraction.on('translateend', translateEndHandler);

			// Return cleanup function to remove all listeners
			return () => {
				modifyInteraction.un('modifystart', modifyStartHandler);
				modifyInteraction.un('modifyend', modifyEndHandler);
				translateInteraction.un('translatestart', translateStartHandler);
				translateInteraction.un('translateend', translateEndHandler);
			};
		},
		[]
	);

	const initSingleImageLayer = useCallback(async () => {
		if (activeImage === null || isInitiated.current || !map) return;
		isInitiated.current = true;

		const layer = new ImageLayer({
			source: null,
			visible: true,
			zIndex: 20,
			name: 'Annotate photos layer',
			properties: { customLayerId: layerId },
		});
		map.addLayer(layer);
		setImageLayer(layer);

		// Create the draw layer and interaction
		const drawSetup = addDrawLayer({
			map: map,
			drawType,
			drawTool: annotationDrawTool,
			colorOptions,
			isRotateEnabled,
		});
		setDrawLayer(drawSetup);
		addDrawingInteraction({ drawSetup, layer });

		// Setup copy-paste keyboard shortcuts, clean up previous listeners
		if (copyPasteCleanup.current) copyPasteCleanup.current();

		// Simple toast function
		const showCopyPasteToast = (message, type) => {
			addToast({
				id: `copy-paste-${Date.now()}`,
				className: type === 'success' ? 'bg-success' : 'bg-warning',
				title: type === 'success' ? 'Copy-Paste' : 'Note',
				message,
				autohide: true,
			});
		};

		copyPasteCleanup.current = setupCopyPasteShortcuts(
			map,
			drawSetup.source,
			drawSetup.getSelectedFeature,
			showCopyPasteToast
		);

		// Attach listeners to mark features as modified and track selected feature
		// Store cleanup function for later use
		modifyListenersCleanup.current = attachModifyListeners(
			drawSetup.modify,
			drawSetup.translate,
			drawSetup.setSelectedFeature
		);

		// Add select interaction
		drawSetup?.select?.on('select', e => {
			// Remove the selected feature
			const selected = e.selected?.[0];
			if (selected) {
				deleteAnnotation({
					feature: selected,
					source: drawSetup.source,
				});
			}
		});

		onChangeImage(layer, activeImage.index);

		// Hide all layers in the regular map, because we are not using it
		mapObject
			?.getLayers()
			?.getArray()
			?.forEach(layer => layer.setVisible(false));
		// setLayersVisibilityByCustomIds(
		// 	mapObject,
		// 	['singleImageLayer', 'mapTilesLayer'],
		// 	false
		// );

		// Add loading spinner
		map.on('loadstart', function () {
			if (isLoadingFullResImage.current) return;
			addMapLoadingSpinner(map);
			setDisableTools(true);
		});
		// Remove loading spinner
		map.on('loadend', function () {
			removeMapLoadingSpinner(map);
			setDisableTools(false);
		});
	}, [map, imagesInAreaOfInterest, activeImage]);

	// Starts the right annotation type
	useEffect(() => {
		initSingleImageLayer();
	}, [initSingleImageLayer]);

	useEffect(() => {
		if (annotationSidebarData) {
			// Update the active class id ref
			activeClassId.current = annotationSidebarData?.activeClassId ?? 0;

			// if erasor is active when changing the annotation type
			// we need to start drawing again
			if (!drawing) startDraw();
		}
	}, [annotationSidebarData]);

	useEffect(() => {
		addExistingAnnotationsToMap();
	}, [activeImage]);

	useEffect(() => {
		const { draw, source } = drawLayer || {};
		if (draw) {
			const newDraw = setDrawInteraction({
				map: map,
				draw,
				source: source,
				drawType: drawType,
				drawTool: annotationDrawTool,
				colorOptions,
				activeClassId: annotationSidebarData?.activeClassId ?? 0,
			});

			const newDrawSetup = { ...drawLayer, draw: newDraw };

			setDrawLayer(newDrawSetup);
			addDrawingInteraction({
				drawSetup: newDrawSetup,
				layer: imageLayer,
			});
		}
	}, [annotationDrawTool, annotationSidebarData]);

	// Update modify interaction when rotation toggle changes
	useEffect(() => {
		const { modify, source, translate, setSelectedFeature } = drawLayer || {};
		if (modify && map) {
			// Clean up old listeners before removing the interaction
			if (modifyListenersCleanup.current) {
				modifyListenersCleanup.current();
				modifyListenersCleanup.current = null;
			}

			// Remove old modify interaction
			map.removeInteraction(modify);

			// Create new modify interaction with updated isRotateEnabled
			const newModify = setModifyInteraction({
				source: source,
				updateModifiedObjects: () => {},
				isRotateEnabled: isRotateEnabled,
			});
			map.addInteraction(newModify);

			// Re-attach all listeners using the extracted helper
			// Note: setSelectedFeature is a stable reference from addDrawLayer that updates
			// the closure variables, ensuring copy/paste always has access to current selection
			modifyListenersCleanup.current = attachModifyListeners(
				newModify,
				translate,
				setSelectedFeature
			);

			// Update drawLayer with new modify interaction
			setDrawLayer({ ...drawLayer, modify: newModify });
		}
	}, [isRotateEnabled, attachModifyListeners]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (imageAbortControllerRef.current) imageAbortControllerRef.current.abort();
			if (copyPasteCleanup.current) {
				copyPasteCleanup.current();
				copyPasteCleanup.current = null;
			}
			if (modifyListenersCleanup.current) {
				modifyListenersCleanup.current();
				modifyListenersCleanup.current = null;
			}
		};
	}, []);

	return (
		<>
			<MapView map={map} setMap={setMap} />
			<AnnotationToolBar aria-label="Annotation tools">
				<Tooltip
					id="annotate-tip"
					variant="light"
					render={({ content, activeAnchor }) => (
						<span>
							{content}{' '}
							<KeyboardShortcut>
								{activeAnchor?.getAttribute('data-tooltip-keyboardshortcut')}
							</KeyboardShortcut>
						</span>
					)}
				/>

				<div>
					<UtilityButton
						label="Exit"
						tooltip={{
							id: 'annotate-tip',
							content: 'Exit annotation mode',
							place: 'top',
						}}
						onClick={handleExit}
						variant="dark"
						keyboardShortcutLabel="ESC"
					/>
				</div>

				<AnnotationDrawTools
					startDraw={startDraw}
					startErase={startErase}
					drawing={drawing}
					disabled={loading || disableTools}
					forceRemoveToolsArray={!isDSPMode ? [] : ['Circle']}
				/>

				<div className="d-flex gap-3 align-items-center">
					{!isDSPMode && (
						<div>
							Annotating {activeImage?.index + 1} / {imagesInAreaOfInterest.length}
						</div>
					)}
					<ButtonGroup>
						{!isDSPMode ? (
							<>
								<StartAITrainingButton
									taskId={pickedTask.model_uuid}
									activeImage={activeImage}
									endAnnotation={endAnnotation}
									loading={loading}
									drawLayer={drawLayer}
								/>
							</>
						) : (
							<UtilityButton
								label="Save and apply"
								tooltip={{
									id: 'annotate-tip',
									content:
										'Apply changes to detections and save as training data',
									place: 'top',
								}}
								onClick={saveAndApply}
								loading={loading}
								variant="dark"
								icon={() => <BiSave />}
							/>
						)}

						{activeImage?.index < imagesInAreaOfInterest.length - 1 && (
							<>
								<UtilityButton
									label="Skip"
									tooltip={{
										id: 'annotate-tip',
										content: 'Ignore the current image and move to the next',
										place: 'top',
									}}
									disabled={loading || disableTools}
									loading={loading}
									onClick={() => nextImage({ skip: true })}
									variant="dark"
									keyboardShortcutLabel="␣"
									icon={() => <BsSkipEnd />}
								/>
								<UtilityButton
									label="Next Image"
									tooltip={{
										id: 'annotate-tip',
										content:
											'Save the annotations in the current image and move to the next tile',
										place: 'top',
									}}
									disabled={loading || disableTools}
									loading={loading}
									onClick={nextImage}
									variant="success"
									keyboardShortcutLabel="↵"
								/>
							</>
						)}
					</ButtonGroup>
				</div>
			</AnnotationToolBar>
		</>
	);
}

const StartAITrainingButton = ({ taskId, endAnnotation, loading, activeImage, drawLayer }) => {
	const { dispatch } = useProject();
	const { addToast } = useToast();
	const { confirm } = useConfirm();

	const { data: annotationInfo } = useQuery({
		queryKey: ['annotationInfo', taskId, activeImage],
		queryFn: () => getAnnotationInfoForModel(taskId),
		enabled: !!taskId,
		retry: 0,
	});

	const {
		can_be_trained: canBeTrained,
		total_number_of_annotations: existingAnnotations,
		total_number_of_tiles: annotatedTiles,
	} = annotationInfo || { can_be_trained: true };

	const startAITraining = useCallback(async () => {
		const checkTrainingData = () => {
			try {
				const { source: drawSource } = drawLayer || {};

				const features = drawSource.getFeatures();

				const totalAnnotations = (features?.length ?? 0) + existingAnnotations;
				const totalTiles = annotatedTiles + 1;

				// If the data can not be checked, we let them try to start training
				if (!totalAnnotations || !totalTiles) return true;

				// If there is not enough data, we show a warning
				if (totalAnnotations < 10 && totalTiles < 6) {
					addToast({
						id: `training_warning`,
						className: 'bg-warning',
						autohide: false,
						title: `Not enough data to start training`,
						message: (
							<>
								You need to have at least <strong>10 annotations</strong> and{' '}
								<strong>6 annotated</strong> tiles to start training.
							</>
						),
					});

					return false;
				}

				return true;
			} catch (error) {
				console.warn(
					'Could not check if data can be trained. Letting the backend decide.',
					error
				);
				return true;
			}
		};

		if (!canBeTrained && !checkTrainingData()) return;

		const userConfirmed = await confirm({
			title: 'Start AI training',
			message: (
				<p>
					Are you sure you want to end the annotation session? Your data will be saved and
					AI training will start.
				</p>
			),
		});

		if (userConfirmed) endAnnotation();
	}, [annotationInfo, dispatch, endAnnotation]);

	return (
		<UtilityButton
			label="Start AI training"
			tooltip={{
				id: 'annotate-tip',
				content: 'End annotation session and start AI training',
				place: 'top',
			}}
			onClick={startAITraining}
			loading={loading}
			variant="dark"
			icon={() => <VscWand />}
		/>
	);
};
